// src/stores/authstore.js
import { defineStore } from "pinia";
import { jwtDecode } from "jwt-decode"; // Importación corregida para jwt-decode v4+
import travelApi from "@/services/httpaxios";

export const useAuthStore = defineStore("auth", {
  state: () => ({
    token: sessionStorage.getItem("authToken") || null,
    expiration: sessionStorage.getItem("authExpiration") || null,
    user: JSON.parse(sessionStorage.getItem("authUser")) || null, // Guardamos el perfil del usuario
    error: null,
    loading: false,
  }),
  getters: {
    isAuthenticated: (state) => !!state.token,
    // Nuevo getter: es true solo si hay un usuario con datos (no es un invitado)
    isUserLoggedIn: (state) => {
      if (!state.token) {
        return false;
      }
      try {
        const decodedToken = jwtDecode(state.token);
        // Un usuario real tiene 'RegisteredMoney' o 'RegisteredPoints'. Un invitado tiene 'GuestMoney'.
        return decodedToken.UserType !== "GuestMoney";
      } catch (error) {
        console.error("Error decodificando el token:", error);
        return false; // Si el token es inválido, no es un usuario logueado.
      }
    },
    // Nuevo getter: es true solo si el usuario es de tipo 'RegisteredPoints'.
    isPointsUser: (state) => {
      if (!state.token) {
        return false;
      }
      try {
        const decodedToken = jwtDecode(state.token);
        return decodedToken.MoneyType === "Points";
      } catch (error) {
        return false;
      }
    },
    // Nuevo getter para extraer la configuración de la empresa del token
    companySettings: (state) => {
      if (!state.token) return {};
      try {
        return jwtDecode(state.token);
      } catch (error) {
        return {};
      }
    },
    currentUser: (state) => state.user,
  },
  actions: {
    /**
     * Establece los datos de autenticación en el estado y en localStorage.
     * @param {string} token - El token JWT.
     * @param {string} expiration - La fecha de expiración del token en formato ISO.
     */
    setAuthData(token, expiration, user) {
      this.token = token;
      this.expiration = expiration;
      this.user = user;

      sessionStorage.setItem("authToken", token);
      sessionStorage.setItem("authExpiration", expiration);
      sessionStorage.setItem("authUser", JSON.stringify(user)); // Guardamos el usuario como string JSON
      // Aseguramos que cualquier nueva instancia de axios tenga el header.
      if (travelApi) {
        travelApi.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      }
    },

    /**
     * Limpia los datos de autenticación del estado y localStorage.
     */
    logout() {
      let logoutUrl = null;

      // 1. Intentar obtener la URL de logout del token ANTES de borrarlo.
      if (this.token) {
        try {
          const decodedToken = jwtDecode(this.token);
          logoutUrl = decodedToken.UrlLogOut; // El atributo se llama UrlLogOut
        } catch (error) {
          console.error("Error decodificando el token al hacer logout:", error);
        }
      }

      // Si no se encontró la URL en el token, usamos la variable de entorno como respaldo.
      if (!logoutUrl) {
        logoutUrl = process.env.VUE_APP_URL_LOGOUT;
      }

      // 2. Limpiar los datos de autenticación del estado y sessionStorage.
      this.token = null;
      this.expiration = null;
      this.user = null;
      sessionStorage.removeItem("authToken");
      sessionStorage.removeItem("authExpiration");
      sessionStorage.removeItem("authUser");
      if (travelApi) {
        delete travelApi.defaults.headers.common["Authorization"];
      }

      // 3. Devolver la URL de logout para que el componente decida qué hacer.
      return logoutUrl;
    },

    /**
     * Cambia la moneda de la sesión del usuario.
     * @param {string} newCurrency - El código de la nueva moneda (COP o USD).
     */
    async changeCurrency(newCurrency) {
      this.loading = true;
      try {
        const response = await travelApi.put("Auth/changecurrency", {
          NewCurrency: newCurrency,
        });
        
        const newToken = response.data.token;
        if (newToken) {
          const decodedToken = jwtDecode(newToken);
          const newExpiration = new Date(decodedToken.exp * 1000).toISOString();
          // Actualizamos el token y la expiración, manteniendo el objeto de usuario actual.
          this.setAuthData(newToken, newExpiration, this.user);
        }
      } catch (error) {
        alert("Lo sentimos, no es posible cambiar su moneda en este momento, intentelo más tarde");
        console.error("Error en changeCurrency:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Realiza una llamada para autenticar al usuario (invitado, credenciales, etc.).
     * @param {object} payload - Puede contener { userOrEmail, password } o { tokenPoints }. Si es vacío, es invitado.
     */
    async authenticate(payload = {}) {
      this.loading = true;
      this.error = null;
      try {
        const request = {
          UserOrEmail: payload.userOrEmail || "",
          Password: payload.password || "",
          TokenPoints: payload.tokenPoints || "",
        };
        const response = await travelApi.post("Auth/guest", request);

        if (!response.data || !response.data.token) {
          throw new Error(
            "La respuesta de autenticación es inválida o no contiene token.",
          );
        }

        const { token, expiration, profileUser } = response.data;
        /*console.log("API Auth/guest response profileUser:", profileUser);*/
        /*console.log(
          "userPointsBalance from API:",
          profileUser?.userPointsBalance,
        );*/ 
        this.setAuthData(token, expiration, profileUser);
      } catch (error) {
        this.error =
          error.response?.data?.message || "Error en la autenticación.";
        console.error("Error en la autenticación:", error);
        throw error; // Relanzamos el error para que el componente que llama pueda reaccionar.
      } finally {
        this.loading = false;
      }
    },

    /**
     * Actualiza el saldo de puntos del usuario en el estado y en sessionStorage.
     * @param {number} pointsRedeemed - La cantidad de puntos que se redimieron.
     */
    updateUserPointsBalance(pointsRedeemed) {
      if (
        this.user &&
        typeof this.user.userPointsBalance === "number" &&
        pointsRedeemed > 0
      ) {
        const newBalance = this.user.userPointsBalance - pointsRedeemed;
        this.user.userPointsBalance = Math.max(0, newBalance); // Asegura que el saldo no sea negativo

        // Actualizar también en sessionStorage para persistencia en la sesión
        const userProfile = JSON.parse(sessionStorage.getItem("authUser"));
        if (userProfile) {
          userProfile.userPointsBalance = this.user.userPointsBalance;
          sessionStorage.setItem("authUser", JSON.stringify(userProfile));
        }
        /*console.log(
          `Saldo de puntos actualizado. Puntos redimidos: ${pointsRedeemed}. Nuevo saldo: ${this.user.userPointsBalance}`,
        );*/
      }
    },

    /**
     * Registra un nuevo usuario y lo autentica automáticamente.
     * @param {object} payload - Datos del registro { firstName, lastName, email, password, confirmPassword }
     */
    async register(payload) {
      this.loading = true;
      this.error = null;
      try {
        const request = {
          FirstName: payload.firstName,
          LastName: payload.lastName,
          Email: payload.email,
          Password: payload.password,
          ConfirmPassword: payload.confirmPassword,
        };
        // Consumimos la API de registro
        const response = await travelApi.post(
          "Auth/registerguestuser",
          request,
        );

        // La respuesta tiene la misma estructura que el login (token, expiration, profileUser)
        const { token, expiration, profileUser } = response.data;

        // Autenticamos automáticamente con la respuesta
        this.setAuthData(token, expiration, profileUser);
      } catch (error) {
        this.error =
          error.response?.data?.message ||
          "Error en el registro, puede que el email ya esté registrado.";
        console.error("Error en el registro:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Inicia el proceso de recuperación de contraseña enviando un código al email del usuario.
     * @param {object} payload - Debe contener { Email }
     */
    async recoverCredential(payload) {
      this.loading = true;
      this.error = null;
      try {
        const response = await travelApi.post(
          "Auth/recovercredguestuser",
          payload,
        );
        if (response.data !== true) {
          throw new Error("No se pudo iniciar el proceso de recuperación.");
        }
        return true; // Éxito
      } catch (error) {
        this.error =
          error.response?.data?.message ||
          "El correo no se encuentra registrado o hubo un error.";
        console.error("Error en recoverCredential:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Cambia la contraseña del usuario usando un código de verificación.
     * @param {object} payload - Debe contener { Email, Password, VerificationCode }
     */
    async changeRecoveredCredential(payload) {
      this.loading = true;
      this.error = null;
      try {
        const response = await travelApi.post(
          "Auth/changecredrecoverguestuser",
          payload,
        );
        if (response.status !== 200 && response.data !== true) {
          throw new Error("No se pudo cambiar la contraseña.");
        }
        return true;
      } catch (error) {
        this.error =
          error.response?.data?.message ||
          "El código de verificación es incorrecto o ha expirado.";
        console.error("Error en changeRecoveredCredential:", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
  },
});
